import * as tracking from '@utils/tracking.ts'

class MessageEntryStepCustomerLoggedIn {

    private readonly $customerNumberInputWrapperJsHook = 'con_generalinquiry-start__customernumber-input-wrapper';
    private readonly $documentObject: Document
    private readonly $form: HTMLFormElement | null
    private readonly $goBackButton: HTMLButtonElement | null
    private readonly $formContent: HTMLDivElement | null
    private readonly $fileUploadSection: HTMLDivElement | null
    private readonly $messageInputWrapper: HTMLDivElement | null
    private readonly $submitButton: HTMLButtonElement | null
    private wrappersInputsAndConfigs: WrapperInputAndConfig[] = []

    private constructor(documentObject: Document) {
        this.$documentObject = documentObject
        this.$goBackButton = this.$documentObject.querySelector('[data-js-hook="con_generalinquiry-start__customer-message-back-button"]')
        this.$form = this.$documentObject.querySelector('[data-js-hook="con_generalinquiry-start__form"]')
        this.$formContent = this.$form?.querySelector('[data-js-hook="con_generalinquiry-start__form-content"]') || null
        this.$fileUploadSection = this.$form?.querySelector('[data-js-hook="file-upload-section"]') || null
        this.$messageInputWrapper = this.$formContent?.querySelector('[data-js-hook="con_generalinquiry-start__customer-message-input-wrapper"]') || null
        this.$submitButton = this.$form?.querySelector('[data-js-hook="con_generalinquiry-start__customer-submit-button"]') || null
        this.bootstrapInputsAndConfigs()
    }

    private bootstrapInputsAndConfigs(): void {
        const inputWrappers: HTMLDivElement[] = []
        this.$messageInputWrapper && inputWrappers.push(this.$messageInputWrapper)
        inputWrappers.forEach(wrapper => {
            wrapper.dataset.toggleClass = wrapper.getAttribute('data-js-validation-toggle-class') || ''
            wrapper.dataset.primaryPatternNoMatchClass = wrapper.getAttribute('data-js-validation-primary-pattern-nomatch-class') || undefined
            const inputField: HTMLInputElement = wrapper.querySelector('[data-js-hook="con_generalinquiry-start__input"]')!
            const invalidMessage: HTMLSpanElement | null = wrapper.querySelector('[data-js-hook="con_generalinquiry-start-invalid-message"]') || null
            const notallowedMessage: HTMLSpanElement | null = wrapper.querySelector('[data-js-hook="con_generalinquiry-start-notallowed-message"]') || null
            const requiredMessage: HTMLSpanElement | null = wrapper.querySelector('[data-js-hook="con_generalinquiry-start-required-message"]') || null
            const validationConfig: ValidationConfig = JSON.parse(inputField.getAttribute('data-js-validation-config') || '{"min":0, "max": 500}')
            if (validationConfig.pattern) {
                validationConfig.pattern = new RegExp(validationConfig.pattern, 'i')
            }
            if (requiredMessage) {
                validationConfig.requiredNote = requiredMessage.textContent
            }
            if (invalidMessage) {
                validationConfig.invalidNote = invalidMessage.textContent
            }
            if (notallowedMessage) {
                validationConfig.notallowedNote = notallowedMessage.textContent
            }
            this.wrappersInputsAndConfigs.push({
                wrapper: wrapper,
                input: inputField,
                config: validationConfig
            })
        })
    }

    private bindFormErrorHandling(): void {
        this.wrappersInputsAndConfigs.forEach(wrapperInputAndConfig => {
            wrapperInputAndConfig.input.oninput = () => {
                if (this.shouldApplyOnInputFormatting(wrapperInputAndConfig)) {
                    this.applyFormatting(wrapperInputAndConfig)
                }
                wrapperInputAndConfig.config.touched = true
                this.handleFormErrors()
            }

            wrapperInputAndConfig.input.onchange = () => {
                this.applyFormatting(wrapperInputAndConfig)
                wrapperInputAndConfig.config.touched = true
                this.handleFormErrors()
            }

            wrapperInputAndConfig.input.onblur = () => {
                wrapperInputAndConfig.config.touched = true
                this.handleFormErrors()
            }
        })
        this.handleFormErrors()
    }

    private applyFormatting(wrapperInputAndConfig: WrapperInputAndConfig): void {
        const trimmedValue = wrapperInputAndConfig.input?.value?.trim()
        if (this.isCustomerNumberWrapper(wrapperInputAndConfig)) {
            const sanitizedValue: string = trimmedValue?.replace(/\D+/g, '');
            wrapperInputAndConfig.input.value = sanitizedValue?.length > 0 ? sanitizedValue.match(/.{1,4}/g)!.join(' ') : '';
        } else {
            wrapperInputAndConfig.input.value = trimmedValue
        }
    }

    private shouldApplyOnInputFormatting(wrapperInputAndConfig: WrapperInputAndConfig): boolean {
        return this.isCustomerNumberWrapper(wrapperInputAndConfig)
    }

    private isCustomerNumberWrapper(wrapperInputAndConfig: WrapperInputAndConfig): boolean {
        return wrapperInputAndConfig.wrapper.dataset.jsHook === this.$customerNumberInputWrapperJsHook
    }

    private handleFormErrors(callFromSubmit?: boolean): boolean {
        let formHasErrors: boolean = false
        this.wrappersInputsAndConfigs.forEach(wrapperInputAndConfig => {
            const cleanedValue = wrapperInputAndConfig.input?.value?.replace(/\p{C}|\s/gu, '');
            const cleanedSecondaryValue = cleanedValue?.replace(/[^\w+]|_/gu, '')?.replace(/(?!^)\+/g, '');
            const inputLength: number = cleanedValue?.length
            const isOptionalAndEmpty = inputLength == 0 && Object.prototype.hasOwnProperty.call(wrapperInputAndConfig.config, 'optional') && wrapperInputAndConfig.config.optional === true
            const isNotEmpty = inputLength > 0
            const isTooShort = inputLength < wrapperInputAndConfig.config.min
            const isTooLong = inputLength > wrapperInputAndConfig.config.max
            const isOutOfValidRange = !isOptionalAndEmpty && (isTooShort || isTooLong)
            const isNotMatchingPrimaryPattern = isNotEmpty && wrapperInputAndConfig.config.pattern && !wrapperInputAndConfig.config.pattern.test(cleanedValue)
            const isNotMatchingSecondaryPattern = isNotEmpty && wrapperInputAndConfig.config.secondaryPattern && !wrapperInputAndConfig.config.secondaryPattern.test(cleanedSecondaryValue)
            if (isOutOfValidRange || isNotMatchingPrimaryPattern || isNotMatchingSecondaryPattern) {
                formHasErrors = true
            }
            if (callFromSubmit) {
                wrapperInputAndConfig.config.touched = true
            }
            if (wrapperInputAndConfig.input.required && !isNotEmpty && wrapperInputAndConfig.config.touched) {
                this.showRequiredErrorMessage(wrapperInputAndConfig)
            } else if ((wrapperInputAndConfig.input !== this.$documentObject.activeElement && isNotEmpty && isTooShort) || isTooLong) {
                this.showPrimaryErrorMessage(wrapperInputAndConfig)
            } else if (isNotMatchingPrimaryPattern) {
                this.showPrimaryErrorMessage(wrapperInputAndConfig)
            } else if (isNotMatchingSecondaryPattern) {
                this.showSecondaryErrorMessage(wrapperInputAndConfig)
            } else {
                this.showFormFieldAsValid(wrapperInputAndConfig)
            }
        })
        return formHasErrors;
    }

    private showRequiredErrorMessage(wrapperInputAndConfig: WrapperInputAndConfig): void {
        this.toggleErrors(wrapperInputAndConfig, true, wrapperInputAndConfig.config.requiredNote)
    }

    private showPrimaryErrorMessage(wrapperInputAndConfig: WrapperInputAndConfig): void {
        this.toggleErrors(wrapperInputAndConfig, true, wrapperInputAndConfig.config.invalidNote)
    }

    private showSecondaryErrorMessage(wrapperInputAndConfig: WrapperInputAndConfig): void {
        this.toggleErrors(wrapperInputAndConfig, true, wrapperInputAndConfig.config.notallowedNote)
    }

    private showFormFieldAsValid(wrapperInputAndConfig: WrapperInputAndConfig) {
        this.toggleErrors(wrapperInputAndConfig, false)
    }

    private toggleErrors(wrapperInputAndConfig: WrapperInputAndConfig, hasErrors: boolean, messageError?: string | undefined): void {

        if (!hasErrors) {
            wrapperInputAndConfig.input.setAttribute('variant', 'default')
            wrapperInputAndConfig.input.setAttribute('validation-message', '')
            return
        }

        wrapperInputAndConfig.input.setAttribute('variant', 'error')
        wrapperInputAndConfig.input.setAttribute('validation-message', messageError!)

    }

    private initBackNavigation(): void {
        this.$goBackButton && this.$goBackButton.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            history.back()
        })
    }

    private bindSpinnerToSubmitButton(): void {
        if (this.$form == null || this.$submitButton == null) return
        this.$submitButton && this.$submitButton.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopPropagation()
            this.resetTouchedFlags();
            const hasErrors = this.handleFormErrors(true) || this.validationFiles();
            if (!hasErrors) {
                this.registerSubmitEventTracking()
                /*                                                                                */
                this.$form?.dispatchEvent(new Event('submit', {cancelable: true}));
            }
        })
    }

    private resetTouchedFlags(): void {
        this.wrappersInputsAndConfigs.forEach(wrapperInputAndConfig => {
            wrapperInputAndConfig.config.touched = false;
        });
    }


    private registerSubmitEventTracking(): void {
        const eventData: any = {
            contact_Activity: 'send',
            contact_Channel: 'contactform_otto',
            contact_Flow: 'loggedin_noSelection',
        }
        const filesInputElement: HTMLInputElement | null = this.$documentObject.querySelector('[data-js-hook="file-upload-input"]')
        if (filesInputElement && filesInputElement.files) {
            eventData['contact_PhotoCount'] = filesInputElement.files.length
        }
        tracking.sendEventToTrackingServer(eventData)

    }

    private validationFiles(): boolean {
        return document.querySelectorAll('.file-upload-banner--visible').length > 0
    }

    private applyFormattingToAllInputs() {
        this.wrappersInputsAndConfigs.forEach(wrapperInputAndConfig => {
            this.applyFormatting(wrapperInputAndConfig)
        })
    }

    private initFileUploadModule() {
        this.$fileUploadSection?.classList.remove('file-upload--hidden')
    }

    static initialize(documentObject: Document): MessageEntryStepCustomerLoggedIn {
        const page: MessageEntryStepCustomerLoggedIn = new MessageEntryStepCustomerLoggedIn(documentObject)
        page.initBackNavigation()
        page.initFileUploadModule()
        page.bindFormErrorHandling()
        page.bindSpinnerToSubmitButton()
        page.applyFormattingToAllInputs()
        return page
    }
}

export default MessageEntryStepCustomerLoggedIn

interface ValidationConfig {
    min: number
    max: number
    optional?: boolean
    pattern?: RegExp
    secondaryPattern?: RegExp
    requiredNote?: string
    invalidNote?: string
    notallowedNote?: string
    touched?: boolean
}

interface WrapperInputAndConfig {
    wrapper: HTMLDivElement
    input: HTMLInputElement
    config: ValidationConfig
}
